import {
  emaLatest,
  calcObvSlope10,
  upDownVolumeAvgs,
  blendedRsVsSpy,
  hasActiveModule3Top,
  gradeFromScoreAndGate,
  formatNeedsReason,
} from "./checklistCommon.js";

function fail(id, shortLabel, needLabel) {
  return { id, shortLabel, needLabel: needLabel || shortLabel };
}

function passLabel(text) {
  return text;
}

/**
 * Momentum calls lane — checklist + grade (not score threshold).
 */
export function evaluateMomentum(result) {
  const passed = [];
  const failed = [];
  const closes = result?.closes;
  const volumes = result?.volumes;
  const price = result?.price ?? closes?.[0];
  const ema50 = result.sm50 ?? result.ema50 ?? (closes ? emaLatest(closes, 50) : null);
  const ema200 = result.sm200 ?? result.ema200 ?? (closes ? emaLatest(closes, 200) : null);
  const buyScore = result?.buyScore ?? 0;
  const gate = result?.gate?.label ?? "WAIT";
  const rr1 = result?.rr1;
  const gateBlocked = result?.gate?.blocked || gate === "BLOCKED";

  const empty = {
    status: "NONE",
    grade: null,
    passed,
    failed,
    reason: "",
    checklist_failed: [],
  };

  if (price == null || !closes?.length) return empty;

  const trendOk =
    ema50 != null && ema200 != null && price > ema50 && ema50 > ema200;
  if (trendOk) passed.push(passLabel("Trend intact"));
  else failed.push(fail("trend", "Trend not intact", "price above EMA50 & EMA stack"));

  const cmf = result?.cmf;
  const obvSlope = calcObvSlope10(closes, volumes);
  const obvRising = obvSlope != null && obvSlope > 0;
  const cmfOk = cmf != null && cmf > 0;
  const bearishFlowDiv =
    result?.cmfDiv?.bearishDiv === true ||
    result?.obv?.bearishDiv === true ||
    (result?.cmfDiv?.bearish === true);
  const flowOk = (cmfOk || obvRising) && !bearishFlowDiv;
  if (flowOk) passed.push(passLabel("Money flow supportive"));
  else {
    failed.push(
      fail(
        "flow",
        "Distribution / weak flow",
        "positive CMF or rising OBV without bearish divergence"
      )
    );
  }

  const { upAvg, downAvg } = upDownVolumeAvgs(closes, volumes, 10);
  let volOk = upAvg != null && downAvg != null && upAvg >= downAvg;
  const avgVol =
    volumes?.length >= 10
      ? volumes.slice(0, 10).reduce((s, v) => s + (v || 0), 0) / 10
      : null;
  const latestUp = closes.length > 1 && closes[0] > closes[1];
  const lowVolRally =
    latestUp &&
    avgVol != null &&
    avgVol > 0 &&
    (volumes[0] || 0) / avgVol < 0.8;
  if (result?.volDiv?.bearish && result.volDiv.ratio != null && result.volDiv.ratio < 0.8) {
    volOk = false;
  }
  if (lowVolRally) volOk = false;
  if (volOk) passed.push(passLabel("Volume confirms"));
  else {
    failed.push(
      fail(
        "volume",
        "Volume not confirming",
        "volume confirmation (up-day vol ≥ down-day; no low-volume rally)"
      )
    );
  }

  const rsi = result?.rsi;
  const rsiOk = rsi != null && rsi >= 50 && rsi <= 72;
  if (rsiOk) passed.push(passLabel("RSI in momentum band"));
  else failed.push(fail("rsi", "RSI outside 50–72", "RSI 50–72"));

  const rsSpread = blendedRsVsSpy(result);
  const rsOk = rsSpread != null && rsSpread >= 0;
  if (rsOk) passed.push(passLabel("RS vs SPY ≥ 0"));
  else {
    failed.push(
      fail(
        "rs",
        "Lagging vs SPY",
        "RS turning positive vs SPY"
      )
    );
  }

  const topOk = !hasActiveModule3Top(result);
  if (topOk) passed.push(passLabel("No top pattern"));
  else {
    failed.push(
      fail(
        "top_pattern",
        "Top / distribution pattern",
        "no active top pattern"
      )
    );
  }

  const scoreOk = buyScore >= 3;
  if (scoreOk) passed.push(passLabel("Score floor"));
  else failed.push(fail("score_floor", "Score below +3 floor", "score ≥ +3 sanity floor"));

  const nonTrendFails = failed.filter((f) => f.id !== "trend");
  let status = "NONE";
  let reason = "";

  if (!trendOk) {
    status = "NONE";
  } else if (nonTrendFails.length === 0) {
    status = "SETUP";
  } else if (nonTrendFails.length <= 2) {
    status = "FORMING";
    reason = formatNeedsReason(nonTrendFails);
  } else {
    status = "NONE";
  }

  let grade = null;
  if (status === "SETUP" || status === "ENTRY_OK") {
    grade = gradeFromScoreAndGate(result, "long");
  }

  if (status === "SETUP") {
    const gateGo = gate === "GO" && !gateBlocked;
    const rrOk = rr1 != null && rr1 >= 1.5;
    if (gateGo && rrOk) {
      status = "ENTRY_OK";
    } else if (!gateGo && gateBlocked) {
      reason = result.gate?.failedCritical?.[0]?.shortLabel ?? "Gate blocked";
    } else if (!gateGo) {
      reason = gate !== "GO" ? `Gate ${gate}` : "Gate not GO";
    } else if (!rrOk) {
      reason = rr1 != null ? `Reward:risk ${rr1.toFixed(1)}:1 — below 1.5:1` : "Reward:risk unknown";
    }
    grade = gradeFromScoreAndGate(result, "long");
  }

  return {
    status,
    grade,
    passed,
    failed,
    reason,
    checklist_failed: failed.map((f) => f.id),
    rsSpread,
    trendOk,
  };
}
